import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../../firebase/firebaseconfig'; // Ensure path is correct
import { doc, getDoc } from 'firebase/firestore';
// Import Lucide icons
import {
    UserPlus,
    FolderOpen,
    MapPin,
    TrendingUp,
    CheckCircle2,
    Info,
    Zap,
    Hash,
    Calendar
} from 'lucide-react';
import ProjectSidebar from './projectsidebar';

const ProjectDetail = () => {
    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);

    // --- FETCH PROJECT FROM FIREBASE ---
    useEffect(() => {

        const fetchProjectData = async () => {
            try {
                const docRef = doc(db, "projects", projectId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    console.log("hello");
                    setProject({ id: docSnap.id, ...docSnap.data() });

                } else {
                    console.log("No such project found in Database!");
                }
            } catch (error) {
                console.error("Error fetching project:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchProjectData();
    }, [projectId]);

    if (loading) return <div className="container py-20 text-center"><h4>Loading Project Details...</h4></div>;
    if (!project) return <div className="container py-20 text-center"><h4>Project not found.</h4></div>;

    return (
        <section className="project-detail py-10 bg-white">
            <div className="container">
                <div className="product-detail-inner text-center text-lg-start">
                    <div className="row gy-5 gx-md-5">

                        {/* --- LEFT SECTION: MAIN CONTENT --- */}
                        <div className="col-lg-8">
                            <div className="section-left">
                                {/* Dynamic Title */}
                                <h3 className="mb-4 pb-4 border-dashed border-2 border-start-0 border-end-0 border-top-0 fw-bold text-dark">
                                    {project.title}
                                </h3>
                                <div className="project-description mb-5  rounded-4  border border-light">

                                    <p className="text-secondary leading-relaxed mb-0" style={{ whiteSpace: 'pre-line' }}>
                                        {project.desc}
                                    </p>
                                </div>
                                {/* Dynamic Image */}
                                <div className="project-image mb-6">
                                    <img src={project.imageUrl || project.Image} alt={project.title} className="w-100 rounded-4 shadow-sm" style={{ maxHeight: '450px', objectFit: 'cover' }} />
                                </div>

                                {/* Progress Bar Section */}
                                <div className="project-progress-bar p-4 bg-lightgreen rounded-4 mb-4 shadow-sm">
                                    <div className="row align-items-center">
                                        <div className="col-md-10">
                                            <div className="progress bg-darkgreen position-relative overflow-visible" style={{ height: '8px' }}>
                                                <div
                                                    className="progress-bar rounded-start-2 bg-green position-relative overflow-visible"
                                                    style={{ width: `${project.percentage}%` }}
                                                >
                                                    <div className="percentage-wrapper position-absolute p-2 bg-green d-flex align-items-center justify-content-center rounded-circle text-white shadow-sm" style={{ right: '-15px', top: '-12px', width: '35px', height: '35px', fontSize: '10px' }}>
                                                        {project.percentage}%
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-2 mt-3 mt-md-0 text-md-end">
                                            <span className="fw-medium small"><span className="green1 fw-bold">{project.percentage}%</span> Completed</span>
                                        </div>
                                    </div>
                                </div>

                                {/* SITE DESCRIPTION SECTION */}
                                <div className="project-description mb-5 p-4 rounded-4 bg-light border border-light">
                                    <h5 className="fw-bold mb-3 d-flex align-items-center">
                                        <Info size={20} className="green1 me-2" /> Site Description
                                    </h5>
                                    <p className="text-secondary leading-relaxed mb-0" style={{ whiteSpace: 'pre-line' }}>
                                        {project.fullDesc}
                                    </p>
                                </div>

                                {/* Project Summary Boxes */}
                                <div className="project-summary">
                                    <h4 className="mb-4 pb-4 border-dashed border-1 border-start-0 border-end-0 border-top-0 fw-bold">Technical Specifications</h4>
                                    <div className="project-summary-list mb-5">
                                        <div className="row gy-4">

                                            

                                            {/* Box: Timeline */}
                                            <div className="col-md-6">
                                                <div className="summary-box p-4 d-flex align-items-center justify-content-between rounded-4 border border-2 border-lightgreen bg-white shadow-sm hvr-lift">
                                                    <div className="summary-info text-start">
                                                        <h6 className="mb-1 fw-bold text-muted small text-uppercase">Timeline:</h6>
                                                        <span >{project.timeline || 'N/A'}</span>
                                                    </div>
                                                    <div className="bg-green rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                                        <Calendar size={22} className="text-white" />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Box: Client */}
                                            <div className="col-md-6">
                                                <div className="summary-box p-4 d-flex align-items-center justify-content-between rounded-4 border border-2 border-lightgreen bg-white shadow-sm hvr-lift">
                                                    <div className="summary-info text-start">
                                                        <h6 className="mb-1 fw-bold text-muted small text-uppercase">Client:</h6>
                                                        <span >{project.client}</span>
                                                    </div>
                                                    <div className="bg-green rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                                        <UserPlus size={22} className="text-white" />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Box: Kilowatt */}
                                            <div className="col-md-6">
                                                <div className="summary-box p-4 d-flex align-items-center justify-content-between rounded-4 border border-2 border-lightgreen bg-white shadow-sm hvr-lift">
                                                    <div className="summary-info text-start">
                                                        <h6 className="mb-1 fw-bold text-muted small text-uppercase">Capacity:</h6>
                                                        <span >{project.kilowatt} KW</span>
                                                    </div>
                                                    <div className="bg-green rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                                        <Zap size={22} className="text-white" />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Box: Location */}
                                            <div className="col-md-6">
                                                <div className="summary-box p-4 d-flex align-items-center justify-content-between rounded-4 border border-2 border-lightgreen bg-white shadow-sm hvr-lift">
                                                    <div className="summary-info text-start">
                                                        <h6 className="mb-1 fw-bold text-muted small text-uppercase">Location:</h6>
                                                        <span >{project.city}, {project.state}</span>
                                                    </div>
                                                    <div className="bg-green rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                                        <MapPin size={22} className="text-white" />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Box: Status */}
                                            <div className="col-md-6">
                                                <div className="summary-box p-4 d-flex align-items-center justify-content-between rounded-4 border border-2 border-lightgreen bg-white shadow-sm hvr-lift">
                                                    <div className="summary-info text-start">
                                                        <h6 className="mb-1 fw-bold text-muted small text-uppercase">Project Status:</h6>
                                                        <span >{Number(project.percentage) === 100 ? "Completed" : "Active"}</span>
                                                    </div>
                                                    <div className="bg-green rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                                        <TrendingUp size={22} className="text-white" />
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>




                                {/* Execution Roadmap */}
                                <div className="project-execution-steps pb-5">
                                    <h3 className="fw-bold mb-5">Execution Roadmap</h3>
                                    <ul className="list-unstyled">
                                        {project.steps?.map((step, idx) => {
                                            const isCompleted = step.status === 'completed';
                                            const isInProgress = step.status === 'in-progress';

                                            return (
                                                <li key={idx} className="mb-4 d-flex align-items-start border-start border-2 border-lightgreen ps-4 position-relative">
                                                    <div className={`position-absolute start-0 translate-middle-x rounded-circle d-flex align-items-center justify-content-center shadow-sm border-2 fw-bold bg-white`}
                                                        style={{ width: '32px', height: '32px', left: '-1px', zIndex: 2 }}>
                                                        {isCompleted ? (
                                                            <CheckCircle2 size={20} className="text-success" />
                                                        ) : (
                                                            <span className={isInProgress ? "text-warning" : "text-muted"}>{idx + 1}</span>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <h5 className={`fw-bold mb-1 ${isInProgress ? 'text-warning' : isCompleted ? 'text-success' : 'text-muted'}`}>
                                                            {step.title}
                                                            {isInProgress && <span className="badge bg-warning text-dark ms-2 fw-normal" style={{ fontSize: '10px' }}>Active</span>}
                                                        </h5>
                                                        <p className="small text-muted mb-0">{step.desc}</p>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* --- RIGHT SECTION: SIDEBAR --- */}
                        <div className="col-lg-4">
                            <div className="sticky-top" style={{ top: '100px' }}>
                                <ProjectSidebar />
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            <style>{`
        .leading-relaxed { line-height: 1.6; }
        .hvr-lift:hover { transform: translateY(-5px); transition: 0.3s; }
      `}</style>
        </section>
    );
};

export default ProjectDetail;